import React, { useState, useCallback, useRef } from 'react'
import { api } from '../utils/supabase'
import {
  Search, Phone, Hash, User, MapPin, Wifi, WifiOff,
  Circle, AlertCircle, Loader2, ChevronDown, ChevronUp, X
} from 'lucide-react'
import Icon3D from '../components/Icon3D'

const TYPE_CONFIG = {
  toka:    { label: 'TOKA SIM',   color: 'red',     phoneKey: 'msisdn' },
  oui:     { label: 'OUI',        color: 'yellow',  phoneKey: 'mdn'   },
  telcel:  { label: 'Telcel B2B', color: 'blue',    phoneKey: 'phone' },
  allce:   { label: 'ALLCE',      color: 'purple',  phoneKey: 'phone' },
  tracker: { label: 'Tracker GPS',color: 'amber',   phoneKey: 'phone' },
}

const STATUS_ICON = {
  active:  <Wifi    className="w-3.5 h-3.5 text-emerald-400" />,
  online:  <Wifi    className="w-3.5 h-3.5 text-emerald-400" />,
  idle:    <Circle  className="w-3.5 h-3.5 text-yellow-400"  />,
  offline: <WifiOff className="w-3.5 h-3.5 text-red-400"     />,
}

function Badge({ type }) {
  const c = TYPE_CONFIG[type] || { label: type, color: 'gray' }
  const colors = {
    red:    'bg-red-500/20 text-red-400 border-red-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    blue:   'bg-blue-500/20 text-blue-400 border-blue-500/30',
    amber:  'bg-amber-500/20 text-amber-400 border-amber-500/30',
    gray:   'bg-gray-500/20 text-gray-400 border-gray-500/30',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border font-medium ${colors[c.color]}`}>
      {c.label}
    </span>
  )
}

function ResultCard({ item, type }) {
  const [expanded, setExpanded] = useState(false)
  const cfg = TYPE_CONFIG[type] || { label: type, color: 'gray', phoneKey: 'phone' }
  const phone = item[cfg.phoneKey] || item.phone || item.msisdn || item.mdn || '—'
  const borderColors = {
    red: 'border-red-500/20', yellow: 'border-yellow-500/20',
    blue: 'border-blue-500/20', amber: 'border-amber-500/20', gray: 'border-gray-600'
  }

  const extras = Object.entries(item).filter(([k]) =>
    !['id','_source','created_at','updated_at','is_active','metadata'].includes(k)
  )

  return (
    <div className={`card overflow-hidden`}>
      <div className="flex items-center justify-between p-3 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Badge type={type} />
          <div className="flex items-center gap-1.5 min-w-0">
            <Phone className="w-3.5 h-3.5 text-gray-500 shrink-0" />
            <span className="font-mono font-semibold text-white text-sm">{phone}</span>
          </div>
          {item.client_name && (
            <div className="hidden sm:flex items-center gap-1 text-sm text-gray-400 min-w-0">
              <User className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate max-w-[160px]">{item.client_name}</span>
            </div>
          )}
          {(item.iccid || item.line_iccid) && (
            <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 font-mono">
              <Hash className="w-3 h-3 shrink-0" />
              <span className="truncate max-w-[140px]">{item.iccid || item.line_iccid}</span>
            </div>
          )}
          {item.connection_status && (
            <span className="hidden sm:flex items-center gap-1 text-xs text-gray-400">
              {STATUS_ICON[item.connection_status] || <Circle className="w-3.5 h-3.5 text-gray-500" />}
              <span className="capitalize">{item.connection_status}</span>
            </span>
          )}
          {(item.status) && !item.connection_status && (
            <span className={`hidden sm:inline text-xs px-1.5 py-0.5 rounded ${
              item.status.toLowerCase().includes('activ') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-700 text-gray-400'
            }`}>{item.status}</span>
          )}
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="p-1 rounded hover:bg-gray-700 text-gray-500 hover:text-white shrink-0"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {/* Línea cruzada del tracker */}
      {type === 'tracker' && (item._line || item.prev_phone) && (
        <div className="px-3 pb-2 space-y-1.5">
          {item._line && (
            <div className="flex items-center gap-2 text-xs bg-gray-700/50 rounded px-2 py-1.5">
              <span className="text-gray-500 font-medium uppercase tracking-tighter text-[10px]">Actual:</span>
              <Badge type={item._line._source} />
              <span className="font-mono text-white">
                {item._line.msisdn || item._line.mdn || item._line.phone}
              </span>
              {item._line.plan && <span className="text-gray-400 truncate max-w-[150px]">{item._line.plan}</span>}
            </div>
          )}

          {item.prev_phone && (
            <div className="flex items-center gap-2 text-xs bg-gray-900/30 rounded px-2 py-1.5 opacity-70">
              <span className="text-gray-600 font-medium uppercase tracking-tighter text-[10px]">Anterior:</span>
              {item.prev_carrier && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] border font-bold uppercase ${
                  TYPE_CONFIG[item.prev_carrier.toLowerCase()]?.color === 'red'    ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                  TYPE_CONFIG[item.prev_carrier.toLowerCase()]?.color === 'yellow' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' :
                  'bg-gray-700/50 text-gray-400 border-gray-600'
                }`}>
                  {item.prev_carrier}
                </span>
              )}
              <span className="font-mono text-gray-400">{item.prev_phone}</span>
              {item.prev_plan && <span className="text-gray-500 italic truncate max-w-[150px]">{item.prev_plan}</span>}
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div className="border-t border-gray-700 px-3 py-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
          {extras.map(([k, v]) => v != null && v !== '' && (
            <div key={k} className="text-xs">
              <span className="text-gray-500 capitalize">{k.replace(/_/g,' ')}: </span>
              <span className="text-gray-300 font-mono break-all">{String(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Section({ title, color, items, type }) {
  if (!items?.length) return null
  const dot = { red:'bg-red-500', yellow:'bg-yellow-500', blue:'bg-blue-500', amber:'bg-amber-500', gray:'bg-gray-500' }
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot[color]}`} />
        {title} <span className="text-gray-500 font-normal">({items.length})</span>
      </h3>
      <div className="space-y-1.5">
        {items.map((item, i) => <ResultCard key={i} item={item} type={type} />)}
      </div>
    </div>
  )
}

export default function LineSearch() {
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState(null)
  const inputRef = useRef(null)

  const handleSearch = useCallback(async (q = query) => {
    const trimmed = q.trim()
    if (trimmed.length < 2) return
    setLoading(true)
    setError(null)
    try {
      const data = await api.get('/api/search', { q: trimmed })
      if (data.success) setResults(data)
      else setError('Sin resultados')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [query])

  const handleClear = () => {
    setQuery('')
    setResults(null)
    setError(null)
    inputRef.current?.focus()
  }

  const total = results?.total || 0
  const { trackers = [], toka = [], oui = [], telcel = [], allce = [] } = results?.data || {}
  const noResults = results && total === 0

  return (
    <div className="space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Icon3D icon={Search} color="violet" size="md" /> Buscador
        </h1>
        <p className="text-gray-400 text-sm">Busca en trackers, TOKA, OUI, Telcel y ALLCE simultáneamente</p>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Teléfono, ICCID, IMEI, nombre de unidad o cliente..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          className="input input-icon !py-3.5 !text-base !pr-24"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {query && (
            <button onClick={handleClear} className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-500">
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => handleSearch()}
            disabled={loading || query.trim().length < 2}
            className="btn btn-primary !py-1.5 !px-4"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
          </button>
        </div>
      </div>

      {/* Hint chips */}
      {!results && !loading && (
        <div className="flex flex-wrap gap-2">
          {['9516057171','B2B ADVANCE','8952130031'].map(hint => (
            <button key={hint} onClick={() => { setQuery(hint); handleSearch(hint); }}
                             className="chip">
              {hint}
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Summary bar */}
      {results && !noResults && (
        <div className="flex items-center gap-3 text-sm text-gray-400 flex-wrap">
          <span className="text-white font-medium">{total} resultado{total !== 1 ? 's' : ''}</span>
          {trackers.length > 0 && <span className="text-amber-400">{trackers.length} trackers</span>}
          {toka.length     > 0 && <span className="text-red-400">{toka.length} TOKA</span>}
          {oui.length      > 0 && <span className="text-yellow-400">{oui.length} OUI</span>}
          {telcel.length   > 0 && <span className="text-blue-400">{telcel.length} Telcel</span>}
          {allce.length    > 0 && <span className="text-purple-400">{allce.length} ALLCE</span>}
        </div>
      )}

      {/* Results */}
      {results && !noResults && (
        <div className="space-y-5">
          <Section title="Trackers GPS"  color="amber"  items={trackers} type="tracker" />
          <Section title="TOKA SIM"      color="red"    items={toka}     type="toka"    />
          <Section title="OUI"           color="yellow" items={oui}      type="oui"     />
          <Section title="Telcel B2B"    color="blue"   items={telcel}   type="telcel"  />
          <Section title="ALLCE"         color="purple" items={allce}    type="allce"   />
        </div>
      )}

      {noResults && (
        <div className="text-center py-16 card border-dashed">
          <AlertCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Sin resultados para <span className="text-white">"{results.query}"</span></p>
          <p className="text-gray-500 text-sm mt-1">Intenta con otro número, ICCID o nombre</p>
        </div>
      )}

      {!results && !loading && !error && (
        <div className="text-center py-16 card border-dashed">
          <div className="flex justify-center mb-3">
            <Icon3D icon={Search} color="slate" size="xl" />
          </div>
          <p className="text-gray-400">Busca en todos los registros a la vez</p>
          <p className="text-gray-600 text-sm mt-1">Trackers · TOKA · OUI · Telcel · ALLCE</p>
        </div>
      )}
    </div>
  )
}
